import { AfterViewInit, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import {
    AdminRequestHelper,
    Auth0APIConfig,
    AvailableColumns,
    CreateEditUserTypes,
    DealRecord,
    DealSearchColumns,
    GetType,
    IntAdmPermissions,
    UserAccessService,
    ModalCloseConfig,
    ModalConfigData,
    UserActionEnum,
    UserFullUi,
    UserHelperService,
    UserRecord,
    UserSearchColumns,
    SharpAuthorizationExpanded,
} from '../shared';
import { UserService } from '../../core/services/user.service';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { UserAccessModalComponent } from '../user-access-modal/user-access-modal.component';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { ConfigurationService } from '../../core/services/configuration.service';
import { AccessTypes, AuthService } from '../../core/services/auth.service';
import { UserAvailableColumns } from '../user-table/user-table.column.types';
import { DealAvailableColumns } from '../deal-table/deal-table.column.types';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { FeatureFlagService } from '../../core/services/feature-flag/feature-flag.service';
import { FeatureFlagNames } from '../../core/services/feature-flag/feature-flag.types';
import { AlertService } from '../../core/services/alert.service';

interface UserSearchHelper {
    requestHelper: AdminRequestHelper;
    defaultSortField: string;
    defaultSortDir: string;
    records: UserRecord[] | DealRecord[] | SharpAuthorizationExpanded[];
    totalRecordCount: number;
}

@Component({
    selector: 'app-users',
    templateUrl: './users.component.html',
    styleUrls: ['./users.component.scss'],
})
export class UsersComponent implements OnInit, AfterViewInit {
    public readonly tabDealDashboard = 0;
    public readonly tabShareholderPortal = 1;

    public internalAdminModal: MatDialogRef<UserAccessModalComponent>;
    public fetching = false;
    public pageLoading = true;
    public selectedIndexSingleUser = 0;
    public currentSearchHelper: UserSearchHelper;
    // index: 0 = Deal Dashboard, 1 = Shareholder User Access
    public singleUserSearchHelpers: UserSearchHelper[] = Array.from({ length: 2 }, (_, index) => ({
        requestHelper: null,
        defaultSortField: index === this.tabDealDashboard ? 'role' : 'permission',
        defaultSortDir: 'asc',
        records: [],
        totalRecordCount: 0,
    }));
    public multipleUserSearchHelper: UserSearchHelper = {
        requestHelper: null,
        defaultSortField: 'nsContactName',
        defaultSortDir: 'asc',
        records: [],
        totalRecordCount: 0,
    };

    // Single User ng-component
    public selectedDealId: number = null;
    public selectedUserId: string;
    public selectedCUserId: number;
    public selectedUser: UserFullUi = null;
    public showRevokeAccess: boolean;

    // Create/Edit User Component values.
    public createEditUserMode: CreateEditUserTypes;
    public showCreateUser: boolean = false;
    public userToEdit: UserFullUi;
    public userToEditId: string = null;
    public userToEditCId: number = 0;

    // Permissions
    public permissionCreateUser = IntAdmPermissions.userCreate;
    public permissionAddUser = IntAdmPermissions.userAdd;
    public permissionEditUser = IntAdmPermissions.userEdit;
    public permissionBlockUser = IntAdmPermissions.userBlock;
    public permissionUnblockUser = IntAdmPermissions.userUnblock;
    public permissionDeactivateUser = IntAdmPermissions.userDeactivate;
    public permissionSendPwResetUser = IntAdmPermissions.userSendPwReset;
    public permissionResendInvUser = IntAdmPermissions.userResendInv;

    public userDisplayedColumns: string[] = [
        UserAvailableColumns.NsContactName,
        UserAvailableColumns.FirstName,
        UserAvailableColumns.MiddleName,
        UserAvailableColumns.LastName,
        UserAvailableColumns.Email,
        UserAvailableColumns.Status,
        UserAvailableColumns.Products,
        UserAvailableColumns.Actions,
    ];
    public dealDisplayedColumns: string[] = [
        DealAvailableColumns.Name,
        DealAvailableColumns.Seller,
        DealAvailableColumns.Buyer,
        DealAvailableColumns.Role,
        DealAvailableColumns.ClosingDate,
        DealAvailableColumns.Actions,
    ];
    public shareholderDealUserDisplayedColumns: string[] = [
        AvailableColumns.Deal,
        AvailableColumns.Permission,
        AvailableColumns.Shareholder,
        AvailableColumns.Actions,
    ];
    public sharpAuthSearchFields: string[] = [AvailableColumns.Deal, AvailableColumns.Shareholder];

    private authStatusTooltip = {
        existing: 'The user has been created, but has not yet been invited.',
        invited: 'The user has been invited, but has not completed account setup.',
        active: 'The user has set up password and MFA methods, and can access available SRSA products.',
        blocked: 'The user is prevented from accessing SRSA applications. Unblock the user to restore access.',
        deactivated:
            'The user has been removed, and cannot access SRSA applications. Deactivated users cannot be reactivated.',
    };
    private auth0StatusTooltipCreated = 'The user has been created, but has not yet been invited.';
    private auth0StatusTooltipInvited = 'The user has been invited, but has not completed account setup.';
    private auth0StatusTooltipActive =
        'The user has set up password and MFA methods, and can access available SRSA products.';
    private auth0StatusTooltipBlocked =
        'The user is prevented from accessing SRSA applications. Unblock the user to restore access.';
    private auth0StatusTooltipDeactivated =
        'The user has been removed, and cannot access SRSA applications. Deactivated users cannot be reactivated.';

    private readonly usersUrl = '/user-access/users';
    private readonly activityUrl = '/user-access/activity';
    private readonly shareholderUrl = '/user-access/shareholders';

    private modalConfigTemplate: MatDialogConfig = {
        panelClass: 'dialog__no-padding',
        disableClose: true,
        minWidth: '500px',
        minHeight: '200px',
        autoFocus: 'first-header',
    };

    constructor(
        private userAccessService: UserAccessService,
        private userService: UserService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private userHelperService: UserHelperService,
        private matDialog: MatDialog,
        private configurationService: ConfigurationService,
        private authService: AuthService,
        private cdr: ChangeDetectorRef,
        private featureFlagService: FeatureFlagService,
        private alert: AlertService,
    ) {}

    ngOnInit(): void {
        this.initializeComponentProperties();

        this.activatedRoute.queryParams.subscribe({
            next: (params: Params) => {
                if (!params) {
                    return;
                }
                this.updateCurrentSearchHelper(params['userId'], params['cUserId'], params['tabSelectedSingleUser']);
                const isEmpty = params && Object.keys(params).length === 0;

                if (isEmpty || params['clear']) {
                    this.selectedUserId = null;
                    this.selectedCUserId = null;
                    this.selectedUser = null;
                    this.selectedIndexSingleUser = 0;

                    this.showCreateUser = false;

                    // If the main Users menu item was clicked in the sidebar then clear any prior search value.
                    this.multipleUserSearchHelper.requestHelper.srchVal = '';

                    this.currentSearchHelper.requestHelper.uid = null;
                    this.currentSearchHelper.requestHelper.uidc = 0;
                    this.currentSearchHelper.requestHelper.sortField = this.currentSearchHelper.defaultSortField;
                    this.currentSearchHelper.requestHelper.sortDir = this.currentSearchHelper.defaultSortDir;
                    this.pageLoading = false;
                    return;
                }

                this.currentSearchHelper.requestHelper.srchVal = params['search'] ?? '';
                this.currentSearchHelper.requestHelper.sortDir =
                    params['sortDir'] ?? this.currentSearchHelper.defaultSortDir;
                this.currentSearchHelper.requestHelper.sortField =
                    params['sortField'] ?? this.currentSearchHelper.defaultSortField;
                this.currentSearchHelper.requestHelper.page = params['page'] ?? 0;
                this.currentSearchHelper.requestHelper.size = params['pageSize'] ?? 100;

                if (params['userId'] || params['cUserId']) {
                    if (params['tabSelectedSingleUser']) {
                        // Using the '+' to force the param to be a number.
                        this.selectedIndexSingleUser = +params['tabSelectedSingleUser'];
                        this.currentSearchHelper.requestHelper.uid = params['userId'];
                        this.currentSearchHelper.requestHelper.uidc = params['cUserId'];

                        if (this.tabDealDashboard === this.selectedIndexSingleUser) {
                            this.fetchDealUserAccess();
                        } else if (this.tabShareholderPortal === this.selectedIndexSingleUser) {
                            this.fetchShareholderUserAccess();
                        }
                    }

                    if (this.selectedUserId !== params['userId'] || this.selectedCUserId !== params['cUserId']) {
                        console.log(
                            '*** UsersComponent.ngOnInit() this.selectedUserId/selectedCUserId is not set; setting & fetching user',
                        );
                        this.selectedUserId = params['userId'];
                        this.selectedCUserId = params['cUserId'];
                        console.log('*** UsersComponent.ngOnInit() fetching single user');
                        this.fetchSingleUser(this.selectedUserId, this.selectedCUserId);
                    } else {
                        console.log(
                            '*** UsersComponent.ngOnInit() param[userId]/param[cUserId] matches current value; ignoring',
                        );
                    }
                } else {
                    this.fetchUsers();
                }
            },
            error: (error) => this.handleError(error),
        });
    }

    ngAfterViewInit(): void {
        this.cdr.detectChanges();
    }

    public performSearch(searchVal: string) {
        this.currentSearchHelper.requestHelper.srchVal = searchVal && searchVal.trim().length > 0 ? searchVal : null;
        this.currentSearchHelper.requestHelper.page = 0;

        this.router.navigate(['/user-access/users'], {
            queryParams: {
                search: searchVal,
                page: 0,
            },
            queryParamsHandling: 'merge',
        });
    }

    public tabChangeSingleUser(e: MatTabChangeEvent) {
        this.updateCurrentSearchHelper(this.selectedUserId, this.selectedCUserId, e.index);

        this.router.navigate([this.usersUrl], {
            queryParams: {
                userId: this.selectedUserId,
                cUserId: this.selectedCUserId,
                tabSelectedSingleUser: e.index,
                search: this.currentSearchHelper.requestHelper.srchVal,
                sortField: this.currentSearchHelper.requestHelper.sortField,
                sortDir: this.currentSearchHelper.requestHelper.sortDir,
                page: this.currentSearchHelper.requestHelper.page,
                pageSize: this.currentSearchHelper.requestHelper.size,
            },
            queryParamsHandling: 'merge',
        });
    }

    public handlePageEvent(e: PageEvent) {
        this.router.navigate(['/user-access/users'], {
            queryParams: {
                page: e.pageIndex,
                pageSize: e.pageSize,
            },
            queryParamsHandling: 'merge',
        });
    }

    public handleSortChange(s: Sort, tab: number | null) {
        if ((tab != null && tab !== this.selectedIndexSingleUser) || (tab == null && this.selectedUserId != null)) {
            return;
        }

        this.router.navigate(['/user-access/users'], {
            queryParams: {
                sortField: s.active,
                sortDir: s.direction,
                clear: null,
            },
            queryParamsHandling: 'merge',
        });
        this.cdr.detectChanges();
    }

    /**
     * Here the $event is the index of the deal record.
     */
    public handleDealActivityNavigation($event) {
        const selectedDeal = this.currentSearchHelper.records[$event] as DealRecord;

        this.router.navigate([this.activityUrl], {
            queryParams: {
                search: selectedDeal.name,
                filterBy: 'deals',
            },
            queryParamsHandling: 'merge',
        });
    }

    public handleDealDashboardNavigation($event) {
        // const selectedDeal = this.dealRecords[$event];
        // this.dealDashboardClick(selectedDeal.id);
    }

    public handleUserAction($event) {
        const selectedUser = this.currentSearchHelper.records[$event.arrayIndex] as UserRecord;
        const userName = selectedUser.firstName + ' ' + selectedUser.lastName;

        switch ($event.action) {
            case UserActionEnum.activity: {
                this.handleUserActivityClick(userName);

                break;
            }
            // case UserActionEnum.resendInv: {
            //     const modalConfig: MatDialogConfig<ModalConfigData> = {
            //         ...this.modalConfigTemplate,
            //         data: {
            //             title: 'Resend Invitation',
            //             messageHTML: `Would you like to resend the Deal Dashboard invitation to <b>${userName}</b>?`,
            //             userName,
            //             dealName: null,
            //             action: UserActionEnum.resendInv,
            //             id: selectedUser.id,
            //         },
            //     };
            //     this.openModal(modalConfig);
            //
            //     break;
            // }
        }
    }

    public canEditUser(status: string): boolean {
        const canEditUser = this.userHelperService.canEditUser(status);
        const perm = this.authService.checkAccess(this.permissionEditUser) === AccessTypes.FULL;
        return canEditUser && perm;
    }

    public canViewUserActivity(status: string): boolean {
        return this.userHelperService.canViewUserActivity(status);
    }

    public canResendInvitation(status: string): boolean {
        const canResendInvitation = this.userHelperService.canResendInvitation(status);
        const perm = this.authService.checkAccess(this.permissionResendInvUser) === AccessTypes.FULL;
        return canResendInvitation && perm;
    }

    public canSendPwReset(status: string): boolean {
        const canSendPwReset = this.userHelperService.canSendPwReset(status);
        const perm = this.authService.checkAccess(this.permissionSendPwResetUser) === AccessTypes.FULL;
        return canSendPwReset && perm;
    }

    public canBlock(status: string): boolean {
        const canBlock = this.userHelperService.canBlock(status);
        const perm = this.authService.checkAccess(this.permissionBlockUser) === AccessTypes.FULL;
        return canBlock && perm;
    }

    public canUnblock(status: string): boolean {
        const canUnblock = this.userHelperService.canUnblock(status);
        const perm = this.authService.checkAccess(this.permissionUnblockUser) === AccessTypes.FULL;
        return canUnblock && perm;
    }

    public canDeactivate(status: string): boolean {
        const canDeactivate = this.userHelperService.canDeactivate(status);
        const perm = this.authService.checkAccess(this.permissionDeactivateUser) === AccessTypes.FULL;
        return canDeactivate && perm;
    }

    public showAddUserMenu(): boolean {
        return (
            this.authService.checkAccess(this.permissionCreateUser) === AccessTypes.FULL ||
            this.authService.checkAccess(this.permissionAddUser) === AccessTypes.FULL
        );
    }

    public handleEditUserClick() {
        this.createEditUserMode = CreateEditUserTypes.EDIT_USER;
        this.userToEdit = this.selectedUser;
        this.showCreateUser = true;
    }

    public async handleUserToEditEvent(e: UserRecord) {
        this.userToEditId = e.id;
        this.userToEditCId = e.nsId;
        this.createEditUserMode = CreateEditUserTypes.EDIT_USER;
        this.showCreateUser = true;
    }

    public handleShowCreateEditUserCancelEvent() {
        this.createEditUserMode = null;
        this.userToEdit = null;
        this.userToEditId = null;
        this.userToEditCId = 0;
        this.showCreateUser = false;
        // If no selected user anymore then fetch users to update the table.
        this.fetchUsers();
    }

    // public handleRevokeAccessEventDD(d: DealRecord) {
    //     this.selectedDealId = d.id;
    //     const modalConfig: MatDialogConfig<ModalConfigData> = {
    //         ...this.modalConfigTemplate,
    //         data: {
    //             title: 'Confirm Revoke Access',
    //             action: UserActionEnum.revokeAccessDD,
    //             messageHTML: "This will remove the user's assigned role to this deal.",
    //             id: this.selectedDealId?.toString(),
    //         },
    //     };
    //     this.openModal(modalConfig);
    // }

    public handleRevokeAccessEventSH(sdu: SharpAuthorizationExpanded) {
        this.selectedDealId = sdu.dealId;
        const modalConfig: MatDialogConfig<ModalConfigData> = {
            ...this.modalConfigTemplate,
            data: {
                title: 'Confirm Revoke Access',
                action: UserActionEnum.revokeAccessSH,
                messageHTML: "This will remove the shareholder's assigned role to this deal and user.",
                userAccessRecord: sdu,
            },
        };
        this.openModal(modalConfig);
    }

    // public handleRevokeAllAccessClick() {
    //     const modalConfig: MatDialogConfig<ModalConfigData> = {
    //         ...this.modalConfigTemplate,
    //         data: {
    //             title: 'Confirm Revoke All Access',
    //             action: UserActionEnum.revokeAllAccess,
    //             messageHTML: 'This will remove roles to all deals this user currently has been assigned.',
    //             id: this.selectedUserId,
    //         },
    //     };
    //     this.openModal(modalConfig);
    // }

    public handleUserActivityClick(name?: string) {
        this.router.navigate([this.activityUrl], {
            queryParams: {
                search: name ? name : this.selectedUser.userUi.firstName + ' ' + this.selectedUser.userUi.lastName,
                filterBy: 'users',
            },
            queryParamsHandling: 'merge',
        });
    }

    // public handleResendInvitation() {
    //     const name = this.selectedUser.userUi.firstName + ' ' + this.selectedUser.userUi.lastName;
    //
    //     const modalConfig: MatDialogConfig<ModalConfigData> = {
    //         ...this.modalConfigTemplate,
    //         data: {
    //             title: 'Resend Invitation',
    //             messageHTML: `Would you like to resend the Deal Dashboard invitation to <b>${name}</b>?`,
    //             userName: name,
    //             dealName: null,
    //             action: UserActionEnum.resendInv,
    //             id: this.selectedUser.userUi.id,
    //         },
    //     };
    //     this.openModal(modalConfig);
    // }

    public handleSendPwResetClick() {
        const name = this.selectedUser.userUi.firstName + ' ' + this.selectedUser.userUi.lastName;

        const modalConfig: MatDialogConfig<ModalConfigData> = {
            ...this.modalConfigTemplate,
            data: {
                title: 'Confirm Password Reset',
                messageHTML: `This will unset the user's current password, and prompt them to register a new password.`,
                userName: name,
                dealName: null,
                action: UserActionEnum.sendPwReset,
                id: this.selectedUser.userUi.id,
                email: this.selectedUser.userUi.email,
            },
        };
        this.openModal(modalConfig);
    }

    public handleBlockClick() {
        const name = this.selectedUser.userUi.firstName + ' ' + this.selectedUser.userUi.lastName;

        const modalConfig: MatDialogConfig<ModalConfigData> = {
            ...this.modalConfigTemplate,
            data: {
                title: 'Confirm Block User',
                messageHTML: `This will prevent users from using their credentials to enter SRSA applications. Users can be unblocked.`,
                userName: name,
                dealName: null,
                action: UserActionEnum.block,
                id: this.selectedUser.userUi.id,
            },
        };
        this.openModal(modalConfig);
    }

    public handleUnblockClick() {
        const name = this.selectedUser.userUi.firstName + ' ' + this.selectedUser.userUi.lastName;

        const modalConfig: MatDialogConfig<ModalConfigData> = {
            ...this.modalConfigTemplate,
            data: {
                title: 'Confirm Unblock User',
                messageHTML: `This will unset the user's current password, and prompt them to register a new password.`,
                userName: name,
                dealName: null,
                action: UserActionEnum.unblock,
                id: this.selectedUser.userUi.id,
            },
        };
        this.openModal(modalConfig);
    }

    public handleDeactivateClick() {
        const name = this.selectedUser.userUi.firstName + ' ' + this.selectedUser.userUi.lastName;

        const modalConfig: MatDialogConfig<ModalConfigData> = {
            ...this.modalConfigTemplate,
            data: {
                title: 'Confirm Deactivate User',
                userName: name,
                dealName: null,
                action: UserActionEnum.deactivate,
                id: this.selectedUser.userUi.id,
            },
        };
        this.openModal(modalConfig);
    }

    public createUserClick() {
        this.createEditUserMode = CreateEditUserTypes.CREATE_USER;
        this.showCreateUser = true;
    }

    public handleNewUserButtonClick() {
        this.handleShowCreateEditUserCancelEvent();
        this.createUserClick();
    }

    public openInNewTab(url: string) {
        window.open(url, '_blank').focus();
    }

    public getUserNsUrl(nsId: number): string {
        const nsContactUrl = this.configurationService.envConfig.getValue().nsContactUrl;

        return nsContactUrl && nsId ? nsContactUrl.replace(/INSERTIDHERE/, nsId.toString()) : '';
    }

    public handleUserClickedEventShareholderUserAccess(uid: number) {
        // will need to pass in the real uid when it exists on the record
        this.router.navigate([this.usersUrl], {
            queryParams: { userId: (this.currentSearchHelper.records[0] as UserRecord).id },
            queryParamsHandling: 'merge',
        });
    }

    public handleShareholderClickedEventShareholderUserAccess(sid: number) {
        this.router.navigate([this.shareholderUrl], {
            queryParams: { sid: sid },
            queryParamsHandling: 'merge',
        });
    }

    public deleteAuthenticationMethod(mfaMethodId: string, mfaMethodName: string) {
        const name = this.selectedUser.userUi.firstName + ' ' + this.selectedUser.userUi.lastName;

        const modalConfig: MatDialogConfig<ModalConfigData> = {
            ...this.modalConfigTemplate,
            data: {
                title: 'Confirm MFA Method Removal',
                messageHTML: 'The user will need to use another authentication method until this one is set up again.',
                userName: name,
                mfaMethodId,
                mfaMethodName,
                action: UserActionEnum.resetMfa,
                id: this.selectedUser.userUi.id,
                email: this.selectedUser.userUi.email,
            },
        };
        this.openModal(modalConfig);
    }

    public canAccessTabDD(): boolean {
        return this.featureFlagService.isFeatureFlagEnabled(FeatureFlagNames.intappsUserDetailDealDashboardTab);
    }

    public canAccessTabSH(): boolean {
        return this.featureFlagService.isFeatureFlagEnabled(FeatureFlagNames.intappsUserDetailShareholderPortalTab);
    }

    public getUserDisplayName(selectedUser: UserFullUi): string {
        let displayName = '';

        if (selectedUser?.userUi?.nsContactName) {
            displayName = selectedUser.userUi.nsContactName;
        } else if (
            selectedUser?.userUi?.firstName ||
            selectedUser?.userUi?.middleName ||
            selectedUser?.userUi?.lastName
        ) {
            displayName =
                (selectedUser?.userUi?.firstName ? selectedUser?.userUi?.firstName : '') +
                (selectedUser?.userUi?.middleName ? ' ' + selectedUser?.userUi?.middleName : '') +
                (selectedUser?.userUi?.lastName ? ' ' + selectedUser?.userUi?.lastName : '');
        } else if (selectedUser?.userUi?.email) {
            displayName = selectedUser?.userUi?.email;
        }

        return displayName.trim();
    }

    public getLoginDisplayStatus(userRecord: UserRecord): string {
        let displayValue = 'Created';

        // Only if the user exists in User Admin, signified by having an id.
        if (userRecord.id && userRecord.auth0Id) {
            switch (userRecord?.status?.toLowerCase()) {
                case 'active':
                case 'existing': {
                    displayValue = 'Active';
                    break;
                }
                case 'invited': {
                    displayValue = 'Invited';
                    break;
                }
                case 'blocked': {
                    displayValue = 'Blocked';
                    break;
                }
                case 'deactivated': {
                    displayValue = 'Deactivated';
                    break;
                }
            }
        }

        return displayValue;
    }

    public getLoginStatusTooltip(userRecord: UserRecord): string {
        let displayValue = this.auth0StatusTooltipCreated;

        // Only if the user exists in User Admin, signified by having an id.
        if (userRecord.id && userRecord.auth0Id) {
            switch (userRecord?.status?.toLowerCase()) {
                case 'active':
                case 'existing': {
                    displayValue = this.auth0StatusTooltipActive;
                    break;
                }
                case 'invited': {
                    displayValue = this.auth0StatusTooltipInvited;
                    break;
                }
                case 'blocked': {
                    displayValue = this.auth0StatusTooltipBlocked;
                    break;
                }
                case 'deactivated': {
                    displayValue = this.auth0StatusTooltipDeactivated;
                    break;
                }
            }
        }

        return displayValue;
    }

    private openModal(modalConfig: MatDialogConfig) {
        this.internalAdminModal = this.matDialog.open(UserAccessModalComponent, modalConfig);
        this.internalAdminModal.componentInstance.confirmButtonEvent.subscribe((emittedValue: ModalCloseConfig) => {
            if (emittedValue) {
                this.internalAdminModal.close();

                const auth0Config: Auth0APIConfig = {
                    userId: emittedValue.id,
                    action: modalConfig.data.action,
                    byId: this.userService.user.sub,
                    byName: this.userService.user.name,
                    mfaMethodId: modalConfig.data.mfaMethodId,
                };

                switch (emittedValue.action) {
                    case UserActionEnum.resetMfa: {
                        this.userAccessService.userAuth0(auth0Config).subscribe({
                            next: (res: any) => {
                                if (res.status >= 200 && res.status <= 204) {
                                    this.alert.show({
                                        title: '',
                                        description: `MFA Method Removed.`,
                                        connotation: 'success',
                                        icon: 'check_circle',
                                        duration: 6000,
                                        width: '720px',
                                    });
                                    this.selectedUser.userUi.mfaMethods[modalConfig.data.mfaMethodName] = undefined;
                                } else {
                                    this.alert.show({
                                        title: 'Server Error',
                                        description: `Changes not saved. Please try again.`,
                                        connotation: 'error',
                                        icon: 'error',
                                        duration: 10000,
                                        width: '720px',
                                    });
                                }
                            },
                            error: (error) => this.handleError(error),
                        });

                        break;
                    }
                    case UserActionEnum.sendPwReset: {
                        this.userAccessService.userAuth0(auth0Config).subscribe({
                            next: (res: any) => {
                                if (res.status >= 200 && res.status <= 204) {
                                    this.alert.show({
                                        title: '',
                                        description: `User password reset successful.`,
                                        connotation: 'success',
                                        icon: 'check_circle',
                                        duration: 6000,
                                        width: '720px',
                                    });
                                } else {
                                    this.alert.show({
                                        title: 'Server Error',
                                        description: `Changes not saved. Please try again.`,
                                        connotation: 'error',
                                        icon: 'error',
                                        duration: 10000,
                                        width: '720px',
                                    });
                                }
                            },
                            error: (error) => this.handleError(error),
                        });

                        break;
                    }
                    // case UserActionEnum.resendInv: {
                    //     this.userAccessService.userAuth0(auth0Config).subscribe({
                    //         next: (res: any) => {
                    //             if (res.status >= 200 && res.status <= 204) {
                    //                 this.alert.show(`Invitation to ${modalConfig.data.userName} resent`, undefined, {
                    //                     duration: 6000,
                    //                 });
                    //             } else {
                    //                 this.alert.show({
                    //                     title: 'Server Error',
                    //                     description: `Changes not saved. Please try again.`,
                    //                     connotation: 'error',
                    //                     icon: 'error',
                    //                     duration: 10000,
                    //                     width: '720px',
                    //                 });
                    //             }
                    //         },
                    //         error: (error) => this.handleError(error),
                    //     });
                    //
                    //     break;
                    // }
                    case UserActionEnum.deactivate: {
                        this.userAccessService.userAuth0(auth0Config).subscribe({
                            next: (res: any) => {
                                if (res.status >= 200 && res.status <= 204) {
                                    this.alert.show({
                                        title: '',
                                        description: `User deactivation successful.`,
                                        connotation: 'success',
                                        icon: 'check_circle',
                                        duration: 6000,
                                        width: '720px',
                                    });

                                    if (this.selectedUser) {
                                        this.fetchSingleUser(this.selectedUserId, this.selectedCUserId);
                                    } else {
                                        this.fetchUsers();
                                    }
                                } else {
                                    this.alert.show({
                                        title: 'Server Error',
                                        description: `Changes not saved. Please try again.`,
                                        connotation: 'error',
                                        icon: 'error',
                                        duration: 10000,
                                        width: '720px',
                                    });
                                }
                            },
                            error: (error) => this.handleError(error),
                        });

                        break;
                    }
                    case UserActionEnum.block: {
                        this.userAccessService.userAuth0(auth0Config).subscribe({
                            next: (res: any) => {
                                if (res.status >= 200 && res.status <= 204) {
                                    this.alert.show({
                                        title: '',
                                        description: `Block authentication successful.`,
                                        connotation: 'success',
                                        icon: 'check_circle',
                                        duration: 600000,
                                        width: '720px',
                                    });

                                    if (this.selectedUser) {
                                        this.fetchSingleUser(this.selectedUserId, this.selectedCUserId);
                                    } else {
                                        this.fetchUsers();
                                    }
                                } else {
                                    this.alert.show({
                                        title: 'Server Error',
                                        description: `Changes not saved. Please try again.`,
                                        connotation: 'error',
                                        icon: 'error',
                                        duration: 10000,
                                        width: '720px',
                                    });
                                }
                            },
                            error: (error) => this.handleError(error),
                        });

                        break;
                    }
                    case UserActionEnum.unblock: {
                        this.userAccessService.userAuth0(auth0Config).subscribe({
                            next: (res: any) => {
                                if (res.status >= 200 && res.status <= 204) {
                                    this.alert.show({
                                        title: '',
                                        description: `Unblock authentication successful.`,
                                        connotation: 'success',
                                        icon: 'check_circle',
                                        duration: 6000,
                                        width: '720px',
                                    });

                                    if (this.selectedUser) {
                                        this.fetchSingleUser(this.selectedUserId, this.selectedCUserId);
                                    } else {
                                        this.fetchUsers();
                                    }
                                } else {
                                    this.alert.show({
                                        title: 'Server Error',
                                        description: `Changes not saved. Please try again.`,
                                        connotation: 'error',
                                        icon: 'error',
                                        duration: 10000,
                                        width: '720px',
                                    });
                                }
                            },
                            error: (error) => this.handleError(error),
                        });

                        break;
                    }
                    // case UserActionEnum.revokeAccessDD: {
                    //     const config: RequestDealUserRole = {
                    //         dealId: this.selectedDealId,
                    //         userId: modalConfig.data.id,
                    //         byId: this.userService.user.sub,
                    //         byName: this.userService.user.name,
                    //     };
                    //     this.userAccessService.deleteUserFromDeal(config).subscribe({
                    //         next: (res: any) => {
                    //             if (res.status >= 200 && res.status <= 204) {
                    //                 this.alert.show({
                    //                     title: '',
                    //                     description: `User access revoked.`,
                    //                     connotation: 'success',
                    //                     icon: 'check_circle',
                    //                     duration: 6000,
                    //                     width: '720px',
                    //                 });
                    //             } else {
                    //                 this.alert.show({
                    //                     title: 'Server Error',
                    //                     description: `Changes not saved. Please try again.`,
                    //                     connotation: 'error',
                    //                     icon: 'error',
                    //                     duration: 10000,
                    //                     width: '720px',
                    //                 });
                    //             }
                    //         },
                    //         error: (error) => this.handleError(error),
                    //     });
                    //
                    //     break;
                    // }
                    case UserActionEnum.revokeAccessSH: {
                        this.userAccessService
                            .deleteShareholderUserAccess(modalConfig.data.userAccessRecord)
                            .subscribe({
                                next: (res: any) => {
                                    if (res.status >= 200 && res.status <= 204) {
                                        this.alert.show({
                                            title: '',
                                            description: `User access revoked.`,
                                            connotation: 'success',
                                            icon: 'check_circle',
                                            duration: 6000,
                                            width: '720px',
                                        });
                                    } else {
                                        this.alert.show({
                                            title: 'Server Error',
                                            description: `Changes not saved. Please try again.`,
                                            connotation: 'error',
                                            icon: 'error',
                                            duration: 10000,
                                            width: '720px',
                                        });
                                    }
                                },
                                error: (error) => this.handleError(error),
                            });

                        break;
                    }
                }
            }
        });

        this.internalAdminModal.componentInstance.cancelButtonEvent.subscribe((emittedValue) => {
            if (emittedValue) {
                this.internalAdminModal.close();
            }
        });
    }

    private initializeComponentProperties() {
        this.showRevokeAccess = true;

        this.setupSearchHelper(this.multipleUserSearchHelper, GetType.users, UserSearchColumns);
        this.setupSearchHelper(
            this.singleUserSearchHelpers[this.tabDealDashboard],
            GetType.dealUserAccess,
            DealSearchColumns,
        );
        this.setupSearchHelper(
            this.singleUserSearchHelpers[this.tabShareholderPortal],
            GetType.shareholderUserAccess,
            this.sharpAuthSearchFields,
        );
    }

    private setupSearchHelper(searchHelper: UserSearchHelper, getType: GetType, searchColumns: string[]) {
        searchHelper.requestHelper = new AdminRequestHelper(getType);
        searchHelper.requestHelper.srchFields = searchColumns;
        searchHelper.requestHelper.sortField = searchHelper.defaultSortField;
        searchHelper.requestHelper.sortDir = searchHelper.defaultSortDir;
    }

    // Get the current search helper based on the route
    private updateCurrentSearchHelper(userId: string, cUserId: number, tabSelectedSingleUser: string | number) {
        if (!userId && !cUserId) {
            this.currentSearchHelper = this.multipleUserSearchHelper;
            return;
        }
        const currentTab = Number.isInteger(+tabSelectedSingleUser)
            ? (+tabSelectedSingleUser as 0 | 1)
            : this.tabDealDashboard;
        this.currentSearchHelper = this.singleUserSearchHelpers[currentTab];
    }

    private fetchSingleUser(uid: string, uidc: number, returnUserObj: boolean = false): Promise<UserFullUi | void> {
        this.fetching = true;
        this.selectedUserId = uid;
        this.selectedCUserId = uidc;

        // retrieve all values for a single user record, which has joins to other
        // tables not required by the users table. Since pagination, sorting, and
        // searching are not required, a new request helper is created to leave the
        // original untouched.
        const tempRequestHelper = new AdminRequestHelper(GetType.users);
        tempRequestHelper.uid = uid;
        tempRequestHelper.uidc = uidc;
        tempRequestHelper.getMfa = true;

        return new Promise((resolve) => {
            this.userAccessService.getUsers(tempRequestHelper).subscribe({
                next: (res: any) => {
                    this.fetching = false;
                    if (returnUserObj) {
                        resolve(res.body);
                    } else {
                        this.selectedUser = res.body;
                        this.pageLoading = false;
                        resolve();
                    }
                },
                error: (error) => this.handleError(error),
            });
        });
    }

    /**
     * The "single shareholder" comes from the original list of shareholders,
     * so this search is to retrieve the User Access records for the user.
     * @private
     */
    private fetchShareholderUserAccess() {
        this.fetching = true;

        this.userAccessService
            .getShareholdersUserAccessRecords(this.singleUserSearchHelpers[this.tabShareholderPortal].requestHelper)
            .subscribe({
                next: (res: any) => {
                    this.fetching = false;
                    const contentRange = res.headers.get('content-range'); // "1-3/3"
                    this.singleUserSearchHelpers[this.tabShareholderPortal].totalRecordCount = +contentRange.substring(
                        contentRange.indexOf('/') + 1,
                    );
                    this.singleUserSearchHelpers[this.tabShareholderPortal].records = res.body;
                },
                error: (error) => this.handleError(error),
            });
    }

    private fetchUsers() {
        this.fetching = true;
        this.userAccessService.getUsers(this.multipleUserSearchHelper.requestHelper).subscribe({
            next: (res: any) => {
                this.fetching = false;
                const contentRange = res.headers.get('content-range'); // "1-3/3"
                this.multipleUserSearchHelper.totalRecordCount = +contentRange.substring(contentRange.indexOf('/') + 1);
                this.multipleUserSearchHelper.records = res.body;
            },
            error: (error) => this.handleError(error),
        });
    }

    /**
     * This needs to fetch deals for the selected user, which is not supported
     * by the API at the moment.
     * @private
     */
    private fetchDealUserAccess() {
        this.fetching = true;

        this.userAccessService
            .getDealUserAccessRecords(this.singleUserSearchHelpers[this.tabDealDashboard].requestHelper)
            .subscribe({
                next: (res: any) => {
                    this.fetching = false;

                    const contentRange = res.headers.get('content-range'); // "1-3/3"
                    this.singleUserSearchHelpers[this.tabDealDashboard].totalRecordCount = +contentRange.substring(
                        contentRange.indexOf('/') + 1,
                    );
                    this.singleUserSearchHelpers[this.tabDealDashboard].records = res.body;
                },
                error: (error) => this.handleError(error),
            });
    }

    private handleError(error: HttpErrorResponse): void {
        this.fetching = false;
        error['stack'] ? console.error(error['stack']) : console.error(error);

        this.alert.show({
            title: 'Server Error',
            description: `Changes not saved. Please try again.`,
            connotation: 'error',
            icon: 'error',
            duration: 10000,
            width: '720px',
        });
    }

    public handleImpersonateUser(selectedUserId: string) {
        const url = `${this.configurationService.envConfig.getValue().sharpUrl}/?userId=${selectedUserId}`;
        window.open(url, '_blank');
    }
}
